import { css, Global } from '@emotion/core';
import styled from '@emotion/styled';
import theme from '@ep/theme';
import React from 'react';

const { GATSBY_IS_DOCZ } = process.env;
const staticFolder = GATSBY_IS_DOCZ ? '/static' : '';

export const bold = `
  font-family: ${theme.typography.boldFont};
  font-weight: bold;
`;

export const H1Display = styled.h1`
  ${bold}
  font-size: ${theme.typography.toRem(80)};
  line-height: ${88 / 80};
  color: ${theme.colors.duskBlue};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(50)};
    line-height: ${54 / 50};
  }
`;

export const H1Large = styled.h1`
  ${bold}
  font-size: ${theme.typography.toRem(60)};
  line-height: ${72 / 60};
  color: ${theme.colors.duskBlue};


  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(42)};
    line-height: ${46 / 42};
  }
`;

export const H1 = styled.h1`
  ${bold}
  font-size: ${theme.typography.toRem(50)};
  line-height: ${54 / 50};
  color: ${theme.colors.duskBlue};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(32)};
    line-height: ${36 / 32};
  }
`;

export const H2Display = styled.h2`
  ${bold}
  font-size: ${theme.typography.toRem(60)};
  line-height: ${72 / 60};
  color: ${theme.colors.duskBlue};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(36)};
    line-height: ${40 / 36};
  }
`;

export const H2Medium = styled.h2`
  ${bold}
  font-size: ${theme.typography.toRem(48)};
  line-height: ${58 / 50};
  color: ${theme.colors.duskBlue};


  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(32)};
    line-height: ${36 / 32};
  }
`;

export const H2 = styled.h2`
  ${bold}
  font-size: ${theme.typography.toRem(40)};
  line-height: ${44 / 40};
  color: ${theme.colors.duskBlue};


  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(32)};
    line-height: ${36 / 32};
  }
`;

export const H3 = styled.h3`
  ${bold}
  font-size: ${theme.typography.toRem(34)};
  line-height: ${42 / 34};
  color: ${theme.colors.duskBlue};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(24)};
    line-height: ${30 / 24};
  }
`;

export const H4 = styled.h4`
  ${bold}
  font-size: ${theme.typography.toRem(23)};
  line-height: ${28 / 23};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(20)};
    line-height: ${24 / 20};
  }
`;

export const H5 = styled.h5`
  ${bold}
  font-size: ${theme.typography.toRem(20)};
  line-height: ${24 / 20};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(18)};
    line-height: ${22 / 18};
  }
`;

export const H6 = styled.h6`
  ${bold}
  font-size: ${theme.typography.toRem(18)};
  line-height: ${24 / 20};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(16)};
    line-height: ${22 / 18};
  }
`;

export const Subhead = styled.div`
  font-size: ${theme.typography.toRem(30)};
  line-height: ${45 / 30};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(23)};
    line-height: ${33 / 23};
  }
`;

export const Subhead2 = styled.div`
  font-size: ${theme.typography.toRem(23)};
  line-height: ${32 / 23};

  ${theme.mediaQueries.mobileAndTablet} {
    font-size: ${theme.typography.toRem(20)};
    line-height: ${28 / 20};
  }
`;

export const Body = styled.div`
  font-size: ${theme.typography.toRem(20)};
  line-height: ${32 / 20};
`;

export const Body2 = styled.div`
  font-size: ${theme.typography.toRem(18)};
  line-height: ${26 / 18};
`;

export const Body3 = styled.div`
  font-size: ${theme.typography.toRem(14)};
  line-height: ${20 / 14};
`;

export const Label = styled.div`
  ${bold}
  font-size: ${theme.typography.toRem(20)};
  line-height: ${21 / 17};
  text-transform: uppercase;
`;

export const SecondaryText = styled.div`
  font-size: ${theme.typography.toRem(16)};
  line-height: ${21 / 16};
  color: ${theme.colors.darkGray};
`;

function Typography() {
  return (
    <Global
      styles={css`
        /* Muli Light (200) is deprecated, should be ok to delete soon */
        @font-face {
          font-family: 'MuliLight';
          src: url('${staticFolder}/fonts/Muli-Light.woff2') format('woff2'),
            url('${staticFolder}/fonts/Muli-Light.woff') format('woff');
          font-weight: 200;
          font-style: normal;
        }

        @font-face {
          font-family: 'MuliMedium';
          src: url('${staticFolder}/fonts/Muli-Medium.woff2') format('woff2'),
            url('${staticFolder}/fonts/Muli-Medium.woff') format('woff');
          font-weight: 300;
          font-style: normal;
        }

        @font-face {
          font-family: 'MuliRegular';
          src: url('${staticFolder}/fonts/Muli-Regular.woff2') format('woff2'),
            url('${staticFolder}/fonts/Muli-Regular.woff') format('woff');
          font-weight: 400;
          font-style: normal;
        }

        @font-face {
          font-family: 'MuliSemibold';
          src: url('${staticFolder}/fonts/Muli-SemiBold.woff2') format('woff2'),
            url('${staticFolder}/fonts/Muli-SemiBold.woff') format('woff');
            font-weight: 500;
            font-style: normal;
        }

        /* Bold is deprecated, use Extra Bold (800) or Black (900) as per the style guide */
        @font-face {
          font-family: 'MuliBold';
          src: url('${staticFolder}/fonts/Muli-Bold.woff2') format('woff2'),
            url('${staticFolder}/fonts/Muli-Bold.woff') format('woff');
            font-weight: 700;
          font-style: normal;
        }

        @font-face {
          font-family: 'MuliExtraBold';
          src: url('${staticFolder}/fonts/Muli-ExtraBold.woff2') format('woff2'),
            url('${staticFolder}/fonts/Muli-ExtraBold.woff') format('woff');
          font-weight: 800;
          font-style: normal;
        }

        @font-face {
          font-family: 'MuliBlack';
          src: url('${staticFolder}/fonts/Muli-Black.woff2') format('woff2'),
            url('${staticFolder}/fonts/Muli-Black.woff') format('woff');
          font-weight: 900;
          font-style: normal;
        }

        @font-face {
          font-family: 'ProximaNovaLight';
          src: url('${staticFolder}/fonts/ProximaNova-Light.woff2') format('woff2'),
            url('${staticFolder}/fonts/ProximaNova-Light.woff') format('woff');
          font-weight: 200;
          font-style: normal;
        }

        @font-face {
          font-family: 'ProximaNovaRegular';
          src: url('${staticFolder}/fonts/ProximaNova-Regular.woff2') format('woff2'),
            url('${staticFolder}/fonts/ProximaNova-Regular.woff') format('woff');
          font-weight: 400;
          font-style: normal;
        }

        @font-face {
          font-family: 'ProximaNovaBold';
          src: url('${staticFolder}/fonts/ProximaNova-Bold.woff2') format('woff2'),
            url('${staticFolder}/fonts/ProximaNova-Bold.woff') format('woff');
            font-weight: 700;
          font-style: normal;
        }

        @font-face {
          font-family: 'ProximaNovaSemiBold';
          src: url('${staticFolder}/fonts/ProximaNova-Semibold.woff2') format('woff2'),
            url('${staticFolder}/fonts/ProximaNova-Semibold.woff') format('woff');
            font-weight: 700;
          font-style: normal;
        }

        @font-face {
          font-family: 'ProximaNovaExtraBold';
          src: url('${staticFolder}/fonts/ProximaNova-Extrabold.woff2') format('woff2'),
            url('${staticFolder}/fonts/ProximaNova-Extrabold.woff') format('woff');
          font-weight: 800;
          font-style: normal;
        }

        @font-face {
          font-family: 'ProximaNovaBlack';
          src: url('${staticFolder}/fonts/ProximaNova-Black.woff2') format('woff2'),
            url('${staticFolder}/fonts/ProximaNova-Black.woff') format('woff');
          font-weight: 900;
          font-style: normal;
        }

        @font-face {
          font-family: 'SantanKelapa';
          src: url('${staticFolder}/fonts/SantanKelapa.woff2') format('woff2'),
            url('${staticFolder}/fonts/SantanKelapa.woff') format('woff');
          font-weight: 900;
          font-style: normal;
        }

        body {
          font-family: ${theme.typography.bodyFont};
          font-weight: 400;
          font-size: ${theme.typography.toRem(20)};
          line-height: ${32 / 20};
          color: ${theme.colors.darkBlue};
        }

        h1,
        h2,
        h3,
        h4,
        h5,
        p {
          margin: 0 0 1.5rem;

          :last-child {
            margin-bottom: 0;
          }

          img {
            width: 100%;
            height: auto;
          }
        }
        p {
          margin: 0 0 1 0rem;
        }
        li {
          margin: 0 0 1rem;

          :last-child {
            margin-bottom: 0;
          }
        }

        a {
          color: ${theme.colors.duskBlue};
          text-decoration:none;
        }

        strong {
          font-weight: bold;
        }

        blockquote {
          position: relative;
          margin: 0;
          padding: 5rem 3.75rem 3rem;
          color: ${theme.colors.darkBlueShades[0]};
          font-size: ${theme.typography.toRem(40)};
          line-height: ${50 / 40};

          ::after {
            content: '‘‘';
            display: block;
            position: absolute;
            top: -1.5rem;
            left: -0.5rem;
            color: ${theme.colors.darkBlueShades[2]};
            font-size: 12rem;
            letter-spacing: -1rem;
          }

          ${theme.mediaQueries.mobileAndTablet} {
            padding-right: 0;
          }

          ${theme.mediaQueries.mobile} {
            padding-left: 0;
          }
        }        
        .blue {
          color: ${theme.colors.oceanBlue}
        }
      `}
    />
  );
}

export default Typography;
